import { Check } from "lucide-react";
import { useLang } from "@/lib/i18n";

export const STEPS = [
  { n: 1, nn: "Datoar", en: "Dates" },
  { n: 2, nn: "Rom", en: "Rooms" },
  { n: 3, nn: "Tilpass", en: "Customise" },
  { n: 4, nn: "Gjesteinfo", en: "Details" },
];

/**
 * Checkout progress.
 * Desktop: numbered steps joined by connectors. Mobile: progress bar + "Step x of 4".
 * `reachable` = highest step the guest may jump to.
 */
export const BookingStepper = ({ step, reachable, onGo, className = "" }) => {
  const { tx } = useLang();
  const current = STEPS.find((s) => s.n === step) || STEPS[0];
  const next = STEPS.find((s) => s.n === step + 1);
  return (
    <nav aria-label={tx("Bestillingssteg", "Booking steps")} data-testid="booking-stepper" className={className}>
      {/* mobile */}
      <div className="sticky top-16 z-30 -mx-5 border-b border-border bg-background/95 px-5 py-3 backdrop-blur sm:-mx-8 sm:px-8 lg:hidden" data-testid="booking-stepper-mobile">
        <div className="flex items-baseline justify-between gap-4 text-sm">
          <p className="font-semibold text-foreground"><span className="text-muted-foreground">{tx("Steg", "Step")} {step} {tx("av", "of")} 4 ·</span> {tx(current.nn, current.en)}</p>
          {next && <p className="truncate text-muted-foreground">{tx("Neste:", "Next:")} {tx(next.nn, next.en)}</p>}
        </div>
        <div className="mt-2.5 flex gap-1.5" aria-hidden>
          {STEPS.map((s) => <span key={s.n} className={`h-1.5 flex-1 rounded-full transition-colors ${s.n <= step ? "bg-brand" : "bg-muted"}`} />)}
        </div>
      </div>

      {/* desktop */}
      <ol className="hidden items-center lg:flex">
        {STEPS.map((s, i) => {
          const done = s.n < step;
          const isCurrent = s.n === step;
          const can = s.n <= reachable && !isCurrent;
          return (
            <li key={s.n} className={`flex items-center ${i < STEPS.length - 1 ? "flex-1" : ""}`}>
              <button
                type="button"
                disabled={!can}
                onClick={() => can && onGo(s.n)}
                aria-current={isCurrent ? "step" : undefined}
                data-testid={`booking-stepper-step-${s.n}`}
                className={`group flex items-center gap-3 rounded-full py-1 pr-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${can ? "cursor-pointer" : "cursor-default"}`}
              >
                <span className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold tabular transition-colors ${
                  done ? "bg-foreground text-white group-hover:bg-primary-hover" : isCurrent ? "bg-brand text-white ring-4 ring-brand/15" : "border border-border bg-card text-muted-foreground"
                }`}>
                  {done ? <Check className="h-4 w-4" strokeWidth={2.5} /> : s.n}
                </span>
                <span className={`${isCurrent ? "font-semibold text-foreground" : done ? "font-medium text-foreground/80 group-hover:text-foreground" : "font-medium text-muted-foreground"}`}>{tx(s.nn, s.en)}</span>
              </button>
              {i < STEPS.length - 1 && <span className={`mx-3 h-px flex-1 transition-colors ${done ? "bg-foreground" : "bg-border"}`} aria-hidden />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
